/**
 * WardrobeItemDetailModal
 * ────────────────────────
 * Popup détaillée d'un article du dressing (réutilisable).
 * Affiche : image grande, nom, marque, chips catégorie/couleur/style,
 * section analyse IA complète si ai_metadata.analyzedAt existe.
 *
 * Usage :
 *   <WardrobeItemDetailModal
 *     visible={!!selectedItem}
 *     item={selectedItem}
 *     onClose={() => setSelectedItem(null)}
 *   />
 */
import React from 'react';
import {
  View, Text, TouchableOpacity, Image, ScrollView, StyleSheet, Platform, Pressable, useWindowDimensions,
} from 'react-native';
import AppModal from './AppModal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AIMetadataDetails, { translateColor as translateAIColor, translateTypeNameToEn } from './AIMetadataDetails';
import { useI18n } from '../i18n';

function Badge({ label, color = 'neutral' }) {
  const bg = color === 'accent' ? '#F5E6D3' : '#EEE';
  const fg = color === 'accent' ? '#8B6F3D' : '#555';
  return (
    <View style={{
      paddingHorizontal: 10, paddingVertical: 4,
      backgroundColor: bg, borderRadius: 12,
    }}>
      <Text style={{ fontSize: 11, color: fg, fontWeight: '500' }}>{label}</Text>
    </View>
  );
}

// SmartImage avec fallback proxy (pour Zara & autres CDN capricieux)
// v1.12.523 — resizeMode passé en prop (default contain pour afficher image entière)
function SmartImage({ uri, style, fallbackEmoji, resizeMode = 'contain' }) {
  const [dead, setDead]       = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  if (!uri || dead) {
    return (
      <View style={[style, { backgroundColor: '#F5F5F5', alignItems: 'center', justifyContent: 'center' }]}>
        <Text style={{ fontSize: 72 }}>{fallbackEmoji || '👗'}</Text>
      </View>
    );
  }

  // v1.12.510 — force HTTPS (Mixed Content sur web)
  let safeUri = uri;
  if (typeof safeUri === 'string' && safeUri.startsWith('http://')) safeUri = 'https://' + safeUri.slice(7);

  let originalUri = safeUri;
  if (safeUri.includes('wsrv.nl/?url=') || safeUri.includes('weserv.nl/?url=')) {
    try {
      const match = safeUri.match(/[?&]url=([^&]+)/);
      if (match) originalUri = decodeURIComponent(match[1]);
    } catch {}
  }

  const isZara = originalUri.includes('static.zara.net');
  let src;
  if (attempt === 0) {
    src = isZara ? 'https://wsrv.nl/?url=' + encodeURIComponent(originalUri) + '&w=600' : safeUri;
  } else if (attempt === 1) {
    src = 'https://wsrv.nl/?url=' + encodeURIComponent(originalUri) + '&w=600';
  } else if (attempt === 2) {
    src = 'https://images.weserv.nl/?url=' + encodeURIComponent(originalUri.replace(/^https?:\/\//, '')) + '&w=600';
  } else {
    src = originalUri;
  }

  const finalStyle = Platform.OS === 'web' ? [style, { objectFit: resizeMode }] : style;

  return (
    <Image
      source={{ uri: src }}
      style={finalStyle}
      resizeMode={resizeMode}
      onError={() => {
        if (attempt < 3) setAttempt(attempt + 1);
        else setDead(true);
      }}
    />
  );
}

export default function WardrobeItemDetailModal({ visible, item, onClose }) {
  const insets = useSafeAreaInsets();
  const { t, lang } = useI18n();
  const en = lang === 'en';
  // v1.12.560 — Traduction des catégories FR → EN
  const CAT_KEY = {
    Hauts: 'cat_tops', Bas: 'cat_bottoms', Robes: 'cat_dresses',
    Vestes: 'cat_jackets', Manteaux: 'cat_coats', Chaussures: 'cat_shoes',
    Sacs: 'cat_bags', Accessoires: 'cat_accessories', Bijoux: 'cat_jewelry',
    Maillots: 'cat_swimwear',
  };
  const trCat = (c) => (CAT_KEY[c] && t(CAT_KEY[c])) || c;
  // v1.12.523 — Image grande responsive (même formule que ProductDetailModal)
  const { width: winW, height: winH } = useWindowDimensions();
  const imageHeight = Math.min(winW * 1.25, winH * 0.55);
  // v1.12.545 — Lightbox plein écran pinch-to-zoom
  const [zoomVisible, setZoomVisible] = React.useState(false);

  if (!item) return null;

  const m = item.ai_metadata;
  const isAnalyzed = !!m?.analyzedAt;
  const imgUri = item.image || item.image_url;

  return (
    <AppModal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      {/* v1.12.522 — Pattern Pressable backdrop absolute + View sheet (au lieu de TouchableOpacity
          wrappant le ScrollView qui bloque le scroll iOS) */}
      <View style={s.backdrop}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
        />
        <View
          style={[s.sheet, { paddingBottom: Math.max(30, insets.bottom + 20) }]}
        >
          {/* Handle bar */}
          <View style={s.handle} />

          {/* Bouton fermer */}
          <TouchableOpacity
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={s.closeBtn}
          >
            <Text style={s.closeTxt}>✕</Text>
          </TouchableOpacity>

          <ScrollView
            contentContainerStyle={{ paddingBottom: 20 }}
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
          >
            {/* Hero : image grande contain — v1.12.545 tap pour zoom plein écran */}
            <TouchableOpacity
              activeOpacity={0.95}
              onPress={() => setZoomVisible(true)}
              style={{ width: '100%', height: imageHeight, backgroundColor: '#FAFAFA', marginBottom: 14 }}>
              <SmartImage
                uri={imgUri}
                style={{ width: '100%', height: '100%' }}
                fallbackEmoji={item.emoji}
                resizeMode="contain"
              />
              <View style={{
                position: 'absolute', bottom: 10, right: 10,
                backgroundColor: 'rgba(0,0,0,0.55)',
                paddingHorizontal: 9, paddingVertical: 4, borderRadius: 12,
                flexDirection: 'row', alignItems: 'center', gap: 4,
              }}>
                <Text style={{ fontSize: 11, color: '#FFF' }}>🔍</Text>
                <Text style={{ fontSize: 10, color: '#FFF', fontWeight: '600', letterSpacing: 0.3 }}>Zoom</Text>
              </View>
            </TouchableOpacity>
            <View style={s.hero}>
              {/* v1.12.560 — Nom traduit en EN via ai_metadata.nameEn ou fallback runtime */}
              <Text style={s.heroName} numberOfLines={2}>
                {en
                  ? (item.ai_metadata?.nameEn || item.ai_metadata?.detectedTypeEn || translateTypeNameToEn(item.name))
                  : item.name}
              </Text>
              {!!item.brand && <Text style={s.heroBrand}>{item.brand}</Text>}
            </View>

            {/* Chips — v1.12.560 traduit catégorie + couleur */}
            <View style={s.chipsRow}>
              {!!item.category && <Badge label={trCat(item.category)} color="neutral" />}
              {!!item.color && <Badge label={en ? (translateAIColor(item.color, true) || item.color) : item.color} color="neutral" />}
              {!!item.style && <Badge label={item.style} color="accent" />}
            </View>

            {/* Analyse IA — composant partagé avec ProductDetailModal (v1.12.48) */}
            {isAnalyzed ? (
              <AIMetadataDetails aiMetadata={m} />
            ) : (
              <View style={s.aiPlaceholder}>
                <Text style={{ fontSize: 24, marginBottom: 4 }}>⏳</Text>
                <Text style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>
                  Analyse IA en cours...{'\n'}
                  Les détails apparaîtront une fois l'analyse terminée.
                </Text>
              </View>
            )}
          </ScrollView>
        </View>
      </View>

      {/* v1.12.545 — Lightbox plein écran pinch-to-zoom */}
      {zoomVisible && imgUri && (
        <AppModal
          visible={zoomVisible}
          animationType="fade"
          presentationStyle="overFullScreen"
          transparent
          onRequestClose={() => setZoomVisible(false)}>
          <View style={{ flex: 1, backgroundColor: '#000' }}>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
              maximumZoomScale={4}
              minimumZoomScale={1}
              bouncesZoom
              showsHorizontalScrollIndicator={false}
              showsVerticalScrollIndicator={false}
              pinchGestureEnabled
              centerContent>
              <SmartImage
                uri={imgUri}
                style={{ width: winW, height: winW / 0.7 }}
                fallbackEmoji={item.emoji}
                resizeMode="contain" />
            </ScrollView>
            <Pressable
              onPress={() => setZoomVisible(false)}
              style={{
                position: 'absolute', top: 50, right: 20,
                width: 44, height: 44, borderRadius: 22,
                backgroundColor: 'rgba(255,255,255,0.15)',
                alignItems: 'center', justifyContent: 'center',
              }}>
              <Text style={{ fontSize: 22, color: '#FFF', fontWeight: '300' }}>✕</Text>
            </Pressable>
          </View>
        </AppModal>
      )}
    </AppModal>
  );
}

const s = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#FFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 20,
    paddingTop: 12,
    maxHeight: '90%',
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#DDD',
    alignSelf: 'center',
    marginBottom: 8,
  },
  closeBtn: {
    position: 'absolute',
    top: 14,
    right: 16,
    zIndex: 10,
    padding: 4,
  },
  closeTxt: {
    fontSize: 18,
    color: '#999',
  },
  hero: {
    alignItems: 'center',
    marginTop: 4,
    marginBottom: 14,
  },
  heroImage: {
    width: 180,
    height: 180,
    borderRadius: 12,
    backgroundColor: '#F5F3F0',
    marginBottom: 12,
  },
  heroName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1A1A1A',
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  heroBrand: {
    fontSize: 11,
    color: '#999',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginTop: 4,
  },
  chipsRow: {
    flexDirection: 'row',
    gap: 6,
    justifyContent: 'center',
    marginBottom: 10,
  },
  aiSection: {
    marginTop: 14,
    padding: 14,
    backgroundColor: '#FAF7F0',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#EAE0C6',
  },
  aiBadge: {
    marginLeft: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#E8F5E9',
    borderRadius: 4,
  },
  aiBadgeText: {
    fontSize: 9,
    color: '#2E7D32',
    fontWeight: '600',
  },
  aiBlock: {
    marginBottom: 10,
  },
  aiLabel: {
    fontSize: 10,
    color: '#999',
    letterSpacing: 1,
    marginBottom: 4,
  },
  aiText: {
    fontSize: 13,
    color: '#1A1A1A',
  },
  aiTag: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: '#FFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E0D4B0',
  },
  aiTagText: {
    fontSize: 11,
    color: '#8B7355',
    fontWeight: '500',
  },
  aiScore: {
    fontSize: 16,
    color: '#1A1A1A',
    fontWeight: '500',
  },
  aiNotes: {
    fontSize: 13,
    color: '#1A1A1A',
    fontStyle: 'italic',
    lineHeight: 18,
  },
  aiPlaceholder: {
    marginTop: 14,
    padding: 14,
    backgroundColor: '#F5F5F5',
    borderRadius: 12,
    alignItems: 'center',
  },
});
